<script setup>
import { useI18n } from 'vue-i18n';

defineProps({
  config: {
    type: Object,
    required: true,
  },
  isDefault: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const getDisplayValue = (field, fallback) => {
  if (!field) return fallback;
  if (field.field) return `[${field.field}]`;
  if (field.value) return field.value;
  if (field.prompt) return fallback;
  return fallback;
};
</script>

<template>
  <div class="bg-white dark:bg-slate-900 rounded-lg shadow-sm overflow-hidden">
    <!-- Image placeholder at top -->
    <div
      class="w-full h-32 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 flex items-center justify-center"
    >
      <span class="text-xs text-slate-800 dark:text-slate-100">
        {{
          isDefault
            ? t(
                'LIBRARIES.TEMPLATE_CONFIG.OVERVIEW.DEFAULT_PREVIEW.CARD.BUTTON'
              )
            : getDisplayValue(
                config.media_url,
                t('LIBRARIES.TEMPLATE_CONFIG.PREVIEW_VALUES.GENERATED_IMAGE')
              )
        }}
      </span>
    </div>

    <div class="p-3">
      <!-- Title -->
      <h4
        v-if="config.title || isDefault"
        class="font-semibold text-sm mb-1.5"
        :class="
          isDefault
            ? 'text-slate-400 dark:text-slate-500'
            : 'text-slate-900 dark:text-slate-100'
        "
      >
        {{
          isDefault
            ? t('LIBRARIES.TEMPLATE_CONFIG.OVERVIEW.DEFAULT_PREVIEW.CARD.TITLE')
            : getDisplayValue(
                config.title,
                config.title?.prompt
                  ? t('LIBRARIES.TEMPLATE_CONFIG.PREVIEW_VALUES.AI_TITLE')
                  : t('LIBRARIES.TEMPLATE_CONFIG.PREVIEW_VALUES.SAMPLE_TITLE')
              )
        }}
      </h4>

      <!-- Description -->
      <p
        v-if="config.description || isDefault"
        class="text-xs mb-3 line-clamp-2"
        :class="
          isDefault
            ? 'text-slate-400 dark:text-slate-500'
            : 'text-slate-600 dark:text-slate-400'
        "
      >
        {{
          isDefault
            ? t(
                'LIBRARIES.TEMPLATE_CONFIG.OVERVIEW.DEFAULT_PREVIEW.CARD.DESCRIPTION'
              )
            : config.description?.field
              ? t('LIBRARIES.TEMPLATE_CONFIG.PREVIEW_VALUES.FIELD_CONTENT', {
                  field: config.description.field,
                })
              : getDisplayValue(
                  config.description,
                  config.description?.prompt
                    ? t(
                        'LIBRARIES.TEMPLATE_CONFIG.PREVIEW_VALUES.AI_DESCRIPTION'
                      )
                    : t(
                        'LIBRARIES.TEMPLATE_CONFIG.PREVIEW_VALUES.SAMPLE_DESCRIPTION'
                      )
                )
        }}
      </p>

      <!-- Action Button -->
      <button
        v-if="config.uri || config.label || isDefault"
        class="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded transition-colors"
        :class="
          isDefault
            ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500'
            : 'bg-woot-500 hover:bg-woot-600 text-white'
        "
      >
        {{
          isDefault
            ? t(
                'LIBRARIES.TEMPLATE_CONFIG.OVERVIEW.DEFAULT_PREVIEW.CARD.BUTTON'
              )
            : getDisplayValue(
                config.label,
                t('LIBRARIES.TEMPLATE_CONFIG.PREVIEW_VALUES.VIEW_DETAILS')
              )
        }}
      </button>
    </div>
  </div>
</template>
